/**
 * The sending side of a Nearby offer as a state machine, the way `nearbyGuest.ts` is the receiving side:
 * `step(state, input)` returns the next state and the effects to carry out, and does nothing else. The rule that
 * matters is in one place: the bridge key is released only when the other device has accepted (a message the server
 * relays, so alone it proves nothing) and this person has compared the emoji on both screens and said they match.
 */
import type { OwnerKey } from '@/crypto';
import type { NearbyCloseReason, NearbyPeer } from '@/api/types';
import type { Sas } from '@/lib/nearby';

/** What the other device's side of the stream says about this offer. */
export type PeerEvent =
  | { readonly type: 'answer'; readonly offerId: string; readonly guestPublicKey: string }
  | { readonly type: 'accepted'; readonly offerId: string }
  | { readonly type: 'closed'; readonly offerId: string; readonly reason: NearbyCloseReason };

interface Live {
  readonly peer: NearbyPeer;
  readonly pair: OwnerKey;
  /** Unknown while the server has not yet answered the offer. */
  readonly offerId: string | null;
  readonly guestPublicKey: string | null;
  /** Null until this device has shown its key and worked the four emoji out. */
  readonly emoji: readonly Sas[] | null;
  readonly accepted: boolean;
  /** This person said the emoji match. Only this lets the key go. */
  readonly confirmed: boolean;
  /** Steps that arrive before the server has told us the offer's id; replayed once it has. */
  readonly early: readonly PeerEvent[];
}

type Active<P extends 'offering' | 'compare' | 'sending'> = { readonly phase: P } & Live;

export type OwnerState =
  | { readonly phase: 'list' }
  | Active<'offering'>
  | Active<'compare'>
  | Active<'sending'>
  | { readonly phase: 'sent'; readonly peer: NearbyPeer }
  | { readonly phase: 'closed'; readonly peer: NearbyPeer; readonly reason: NearbyCloseReason | undefined; readonly message?: string | undefined };

export type OwnerInput =
  | { readonly type: 'send'; readonly peer: NearbyPeer; readonly pair: OwnerKey }
  | { readonly type: 'offered'; readonly offerId: string }
  | { readonly type: 'peer'; readonly event: PeerEvent }
  /** This device showed its key and worked the emoji out. */
  | { readonly type: 'revealed'; readonly offerId: string; readonly emoji: readonly Sas[] }
  | { readonly type: 'confirm' }
  | { readonly type: 'sent'; readonly offerId: string }
  | { readonly type: 'failed'; readonly offerId: string | null; readonly reason?: NearbyCloseReason | undefined; readonly message?: string | undefined }
  /** Cancel while offering, "Back to the list" once it ended, or the tab closing. */
  | { readonly type: 'cancel' };

export type OwnerEffect =
  | { readonly type: 'offer'; readonly peer: NearbyPeer; readonly pair: OwnerKey }
  | { readonly type: 'reveal'; readonly offerId: string; readonly pair: OwnerKey; readonly guestPublicKey: string }
  | { readonly type: 'release'; readonly offerId: string; readonly pair: OwnerKey; readonly guestPublicKey: string; readonly peer: NearbyPeer }
  | { readonly type: 'decline'; readonly offerId: string };

export interface OwnerStep {
  readonly state: OwnerState;
  readonly effects: readonly OwnerEffect[];
}

export const IDLE: OwnerState = { phase: 'list' };

const stay = (state: OwnerState): OwnerStep => ({ state, effects: [] });

/** Whether an offer is in progress (the key has not gone and nothing has ended it). */
export function isLive(state: OwnerState): state is Active<'offering'> | Active<'compare'> | Active<'sending'> {
  return state.phase === 'offering' || state.phase === 'compare' || state.phase === 'sending';
}

/** Releases the key if both conditions hold, otherwise stays where it is. */
function maybeRelease(state: Active<'compare'>): OwnerStep {
  if (!state.accepted || !state.confirmed || state.guestPublicKey === null || state.emoji === null || state.offerId === null) return stay(state);
  return { state: { ...state, phase: 'sending' }, effects: [{ type: 'release', offerId: state.offerId, pair: state.pair, guestPublicKey: state.guestPublicKey, peer: state.peer }] };
}

function onPeer(state: Active<'offering'> | Active<'compare'> | Active<'sending'>, event: PeerEvent): OwnerStep {
  if (state.offerId === null) return stay({ ...state, early: [...state.early, event] });
  if (event.offerId !== state.offerId) return stay(state);
  switch (event.type) {
    case 'answer':
      // the guest has committed to its key; only now does the owner show the one it committed to
      if (state.phase !== 'compare' || state.guestPublicKey !== null) return stay(state);
      return { state: { ...state, guestPublicKey: event.guestPublicKey }, effects: [{ type: 'reveal', offerId: state.offerId, pair: state.pair, guestPublicKey: event.guestPublicKey }] };
    case 'accepted':
      return state.phase === 'compare' ? maybeRelease({ ...state, accepted: true }) : stay(state);
    case 'closed':
      return { state: { phase: 'closed', peer: state.peer, reason: event.reason }, effects: [] };
  }
}

export function step(state: OwnerState, input: OwnerInput): OwnerStep {
  switch (input.type) {
    case 'send':
      if (state.phase !== 'list') return stay(state);
      return {
        state: { phase: 'offering', peer: input.peer, pair: input.pair, offerId: null, guestPublicKey: null, emoji: null, accepted: false, confirmed: false, early: [] },
        effects: [{ type: 'offer', peer: input.peer, pair: input.pair }],
      };
    case 'offered': {
      // called off while the server was still answering: the offer exists there now, so it is declined there
      if (state.phase !== 'offering') return { state, effects: [{ type: 'decline', offerId: input.offerId }] };
      let next: OwnerStep = stay({ ...state, phase: 'compare', offerId: input.offerId, early: [] });
      for (const event of state.early) {
        if (event.offerId !== input.offerId || !isLive(next.state)) continue;
        const after = onPeer(next.state, event);
        next = { state: after.state, effects: [...next.effects, ...after.effects] };
      }
      return next;
    }
    case 'peer':
      return isLive(state) ? onPeer(state, input.event) : stay(state);
    case 'revealed':
      return state.phase === 'compare' && state.offerId === input.offerId && state.guestPublicKey !== null && state.emoji === null ? stay({ ...state, emoji: input.emoji }) : stay(state);
    case 'confirm':
      // only a person who has the emoji in front of them, once
      return state.phase === 'compare' && state.emoji !== null && !state.confirmed ? maybeRelease({ ...state, confirmed: true }) : stay(state);
    case 'sent':
      return state.phase === 'sending' && state.offerId === input.offerId ? stay({ phase: 'sent', peer: state.peer }) : stay(state);
    case 'failed': {
      if (!isLive(state) || (input.offerId !== null && state.offerId !== input.offerId)) return stay(state);
      return { state: { phase: 'closed', peer: state.peer, reason: input.reason, message: input.message }, effects: [] };
    }
    case 'cancel': {
      const effects: OwnerEffect[] = isLive(state) && state.offerId !== null ? [{ type: 'decline', offerId: state.offerId }] : [];
      return { state: IDLE, effects };
    }
  }
}
